import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Send, DollarSign, Check, MessageSquare } from 'lucide-react';
import { motion } from 'framer-motion';
import { Commission, ReviewComment } from '../types/commission';
interface ReviewPanelProps {
  commission: Commission;
  onClose: () => void;
  onSubmitCounter: (commissionId: string, loanId: string, message: string) => void;
  onSubmitResolution: (commissionId: string, overrides: {
    loanId: string;
    newCommission: number;
  }[], message: string) => void;
}
export function ReviewPanel({
  commission,
  onClose,
  onSubmitCounter,
  onSubmitResolution
}: ReviewPanelProps) {
  const [loanReplies, setLoanReplies] = useState<{
    [loanId: string]: string;
  }>({});
  const [resolutionMessage, setResolutionMessage] = useState('');
  const [overrides, setOverrides] = useState<{
    [loanId: string]: string;
  }>({});
  const [showResolutionMode, setShowResolutionMode] = useState(false);
  const handleSubmitLoanReply = (loanId: string) => {
    const message = loanReplies[loanId]?.trim();
    if (message) {
      onSubmitCounter(commission.userId, loanId, message);
      setLoanReplies(prev => ({
        ...prev,
        [loanId]: ''
      }));
    }
  };
  const handleSubmitResolution = () => {
    const overrideArray = Object.entries(overrides).filter(([_, value]) => value.trim() !== '').map(([loanId, value]) => ({
      loanId,
      newCommission: parseFloat(value)
    })).filter(({
      newCommission
    }) => !isNaN(newCommission));
    if (resolutionMessage.trim()) {
      onSubmitResolution(commission.userId, overrideArray, resolutionMessage);
      onClose();
    }
  };
  const flaggedLoans = commission.loans.filter(loan => ['flagged', 'under_review', 'resolved_pending_approval'].includes(loan.approvalStatus));
  const calculateNewTotal = () => {
    let total = commission.total;
    Object.entries(overrides).forEach(([loanId, value]) => {
      const loan = commission.loans.find(l => l.loanId === loanId);
      if (loan && value.trim() !== '') {
        const newCommission = parseFloat(value);
        if (!isNaN(newCommission)) {
          total = total - loan.commission + newCommission;
        }
      }
    });
    return total;
  };
  const newTotal = calculateNewTotal();
  const hasChanges = Object.values(overrides).some(v => v.trim() !== '');
  return ReactDOM.createPortal(<motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[110] flex items-center justify-center" onClick={onClose} data-id="element-1117">
      <motion.div initial={{
      y: '100%',
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: '100%',
      opacity: 0
    }} onClick={e => e.stopPropagation()} className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-id="element-1118">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex-shrink-0" data-id="element-1119">
          <div className="flex items-center justify-between mb-4" data-id="element-1120">
            <h2 className="text-2xl font-bold text-slate-900" data-id="element-1121">
              Commission Review
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-1122">
              <X size={24} className="text-slate-500" data-id="element-1123" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-1124">
            <div data-id="element-1125">
              <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-1" data-id="element-1126">
                Loan Officer
              </p>
              <p className="text-sm font-bold text-slate-900" data-id="element-1127">
                {commission.userName}
              </p>
            </div>
            <div data-id="element-1128">
              <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-1" data-id="element-1129">
                Period
              </p>
              <p className="text-sm font-bold text-slate-900" data-id="element-1130">
                {commission.payPeriod}
              </p>
            </div>
          </div>

          {/* Mode Toggle */}
          <div className="mt-4 flex gap-2" data-id="element-1131">
            <button onClick={() => setShowResolutionMode(false)} className={`flex-1 px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${!showResolutionMode ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-1132">
              Discussion
            </button>
            <button onClick={() => setShowResolutionMode(true)} className={`flex-1 px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${showResolutionMode ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-1133">
              Resolve & Override
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-1134">
          {!showResolutionMode ? <>
              {/* Per-Loan Discussion Threads */}
              {flaggedLoans.length > 0 ? <div className="space-y-5" data-id="element-1135">
                  {flaggedLoans.map(loan => <div key={loan.loanId} className="bg-white border-2 border-slate-200 rounded-2xl overflow-hidden" data-id="element-1136">
                      {/* Loan Header */}
                      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between" data-id="element-1137">
                        <div className="flex items-center gap-3" data-id="element-1138">
                          <span className="text-sm font-bold text-slate-900" data-id="element-1139">
                            {loan.loanId}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${loan.approvalStatus === 'under_review' ? 'bg-blue-100 text-blue-700' : loan.approvalStatus === 'resolved_pending_approval' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-1140">
                            {loan.approvalStatus === 'under_review' ? 'Under Review' : loan.approvalStatus === 'resolved_pending_approval' ? 'Resolved' : 'Flagged'}
                          </span>
                        </div>
                        <span className="text-sm font-semibold text-emerald-600 tabular-nums" data-id="element-1141">
                          $
                          {loan.commission.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                        </span>
                      </div>

                      {/* Comment Thread */}
                      <div className="px-5 py-4" data-id="element-1142">
                        {loan.loanReviewComments && loan.loanReviewComments.length > 0 ? <div className="space-y-2.5 mb-4" data-id="element-1143">
                            {loan.loanReviewComments.map((comment, idx) => <motion.div key={comment.id} initial={{
                    opacity: 0,
                    y: 8
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: idx * 0.05
                  }} className={`p-3 rounded-xl text-sm ${comment.authorRole === 'System Administrator' ? 'bg-blue-50 border border-blue-200 ml-6' : 'bg-slate-50 border border-slate-200 mr-6'}`} data-id="element-1144">
                                <div className="flex items-center justify-between mb-1" data-id="element-1145">
                                  <div className="flex items-center gap-2" data-id="element-1146">
                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white ${comment.authorRole === 'System Administrator' ? 'bg-blue-500' : 'bg-slate-500'}`} data-id="element-1147">
                                      {comment.author[0]}
                                    </div>
                                    <span className="font-semibold text-xs text-slate-700" data-id="element-1148">
                                      {comment.author}
                                    </span>
                                  </div>
                                  <span className="text-[10px] text-slate-400" data-id="element-1149">
                                    {comment.timestamp}
                                  </span>
                                </div>
                                <p className="text-slate-700 leading-relaxed pl-7" data-id="element-1150">
                                  {comment.message}
                                </p>
                              </motion.div>)}
                          </div> : <p className="text-sm text-slate-400 italic mb-4" data-id="element-1151">
                            No comments yet
                          </p>}

                        {/* Per-Loan Reply Box */}
                        <div className="flex gap-2" data-id="element-1152">
                          <input type="text" value={loanReplies[loan.loanId] || ''} onChange={e => setLoanReplies(prev => ({
                    ...prev,
                    [loan.loanId]: e.target.value
                  }))} onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmitLoanReply(loan.loanId);
                    }
                  }} placeholder={`Reply to ${loan.loanId}...`} className="flex-1 px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" data-id="element-1153" />
                          <button onClick={() => handleSubmitLoanReply(loan.loanId)} disabled={!loanReplies[loan.loanId]?.trim()} className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 disabled:cursor-not-allowed text-white rounded-xl transition-colors flex items-center gap-1.5" data-id="element-1154">
                            <Send size={14} data-id="element-1155" />
                          </button>
                        </div>
                      </div>
                    </div>)}
                </div> : <div className="text-center py-8 text-slate-500" data-id="element-1156">
                  No flagged loans found.
                </div>}
            </> : <>
              {/* Override Mode */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-id="element-1157">
                <div className="flex items-center gap-2 mb-3" data-id="element-1158">
                  <DollarSign size={18} className="text-emerald-600" data-id="element-1159" />
                  <h3 className="text-sm font-bold text-emerald-900" data-id="element-1160">
                    Override Commission Amounts
                  </h3>
                </div>
                <p className="text-xs text-emerald-700 mb-4" data-id="element-1161">
                  Adjust commission amounts for flagged loans. Leave blank to
                  keep original amount.
                </p>

                <div className="space-y-3" data-id="element-1162">
                  {flaggedLoans.map(loan => <div key={loan.loanId} className="bg-white rounded-lg p-4 border border-slate-200" data-id="element-1163">
                      <div className="flex items-center justify-between mb-2" data-id="element-1164">
                        <span className="text-sm font-bold text-slate-900" data-id="element-1165">
                          {loan.loanId}
                        </span>
                        <span className="text-xs text-slate-500" data-id="element-1166">
                          Original: $
                          {loan.commission.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                        </span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-1167">
                        <span className="text-xs text-slate-600 font-medium" data-id="element-1168">
                          New Amount:
                        </span>
                        <div className="flex-1 relative" data-id="element-1169">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm" data-id="element-1170">
                            $
                          </span>
                          <input type="number" step="0.01" value={overrides[loan.loanId] || ''} onChange={e => setOverrides({
                      ...overrides,
                      [loan.loanId]: e.target.value
                    })} placeholder={loan.commission.toFixed(2)} className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm" data-id="element-1171" />
                        </div>
                      </div>
                    </div>)}
                </div>

                {hasChanges && <div className="mt-4 p-4 bg-white rounded-lg border-2 border-emerald-300" data-id="element-1172">
                    <div className="flex items-center justify-between" data-id="element-1173">
                      <span className="text-sm font-semibold text-slate-700" data-id="element-1174">
                        New Total Commission:
                      </span>
                      <span className="text-lg font-bold text-emerald-700" data-id="element-1175">
                        $
                        {newTotal.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-1" data-id="element-1176">
                      <span className="text-xs text-slate-500" data-id="element-1177">Original:</span>
                      <span className="text-xs text-slate-500 line-through" data-id="element-1178">
                        $
                        {commission.total.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                      </span>
                    </div>
                  </div>}
              </div>

              {/* Resolution Message */}
              <div data-id="element-1179">
                <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-1180">
                  Resolution Notes <span className="text-red-500" data-id="element-1181">*</span>
                </label>
                <textarea value={resolutionMessage} onChange={e => setResolutionMessage(e.target.value)} placeholder="Explain the resolution and any adjustments made..." className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none" rows={4} data-id="element-1182" />
              </div>
            </>}
        </div>

        {/* Footer - only show for Resolution mode */}
        {showResolutionMode && <div className="p-6 border-t border-slate-200 flex gap-3 flex-shrink-0" data-id="element-1183">
            <button onClick={onClose} className="flex-1 px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition-colors" data-id="element-1184">
              Close
            </button>
            <button onClick={handleSubmitResolution} disabled={!resolutionMessage.trim()} className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors" data-id="element-1185">
              <Check size={20} data-id="element-1186" />
              Submit for Approval
            </button>
          </div>}

        {/* Footer for Discussion mode - just a close button */}
        {!showResolutionMode && <div className="p-4 border-t border-slate-200 flex-shrink-0" data-id="element-1187">
            <button onClick={onClose} className="w-full px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition-colors" data-id="element-1188">
              Close
            </button>
          </div>}
      </motion.div>
    </motion.div>, document.body);
}